import React, { useCallback, useEffect, useState } from "react";
import CustomTitle from "../../custom/CustomTitle";
import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomButton from "../../custom/CustomButton";
import CustomCard from "../../custom/CustomCard";
import CustomInput from "../../custom/CustomInput";
import { elevatorListToUpdateUrl, mainUrl, roles } from "../costants";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  getElevator,
  updateElevator,
} from "../../../api/services/elevatorService";
import CustomLoading from "../../custom/CustomLoading";
import { useSnackbar } from "notistack";
import { updateAzElevator } from "../../../api/services/azService";
import CustomSelect from "../../custom/CustomSelect";
import { getUsers } from "../../../api/services/userService";

type UpdateElevatorProps = {
  setBreadcrumb: React.Dispatch<React.SetStateAction<any[]>>;
  role: string;
};

const UpdateElevator: React.FC<UpdateElevatorProps> = ({
  setBreadcrumb,
  role,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [values, setValues] = useState<any>({
    installation: "",
    system_number: "",
    system_serial_number: "",
    address: "",
    id_az: "",
    model: "",
    phone_operator: "",
    azienda: "",
  });

  useEffect(() => {
    setBreadcrumb([
      <Link style={{ color: "#ffffff" }} key="1" to={mainUrl}>
        {t("home")}
      </Link>,
      <Link style={{ color: "#ffffff" }} key="2" to={elevatorListToUpdateUrl}>
        {role === roles.company ? t("updateDeleteDevice") : t("updateDevice")}
      </Link>,
      <Typography color="#ffffff" key="2">
        {values.installation}
      </Typography>,
    ]);
  }, [values]);

  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  useEffect(() => {
    if (role === roles.superadmin || role === roles.admin) {
      getUsers("aziende").then((res) => {
        if (res && res.aziende) {
          setAllCompanies(res.aziende);
        }
      });
    }
  }, [role]);

  useEffect(() => {
    getElevator(id!).then((res) => {
      if (res && res.installation) {
        setValues({
          model: res.installation.model,
          phone_operator: res.installation.phone_operator,
          installation: res.installation.name,
          address: res.installation?.installation_info?.address,
          system_number: res.installation.system_number,
          system_serial_number: res.installation.system_serial_number,
          azienda: res.installation.azienda,
        });
      }
      setLoading(false);
    });
  }, []);

  const handleSubmit = useCallback(() => {
    updateElevator({
      id: id!,
      name: values.installation,
      model: values.model,
      phone_operator: values.phone_operator,
      installation_info: {
        city: "",
        province: "",
        address: values.address,
      },
      sys_num: values.system_number,
      sys_serial_num: values.system_serial_number,
      id_gestionale: values.id_az,
      azienda: values.azienda,
    }).then((res) => {
      if (res && !res.err) {
        enqueueSnackbar(t("updateElevatorSuccess"), {
          variant: "success",
          preventDuplicate: true,
        });
        updateAzElevator(values.system_number, {
          sys_serial_num: values.system_serial_number,
          installation_info: {
            city: "",
            province: "",
            address: values.address,
          },
        });
        navigate(-1);
      } else {
        enqueueSnackbar(res?.err?.message || t("updateElevatorErrror"), {
          variant: "error",
          preventDuplicate: true,
        });
      }
    });
  }, [values]);

  if (loading) {
    return <CustomLoading />;
  }

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      style={{ padding: "100px 32px" }}
    >
      <Grid item xs={12}>
        <CustomTitle
          title={values.installation}
          goBack={() => navigate(elevatorListToUpdateUrl)}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <CustomCard
          content={
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomInput
                  label={t("installationName")}
                  value={values.installation}
                  handleChange={(e) =>
                    setValues({ ...values, installation: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInput
                  label={t("address")}
                  value={values.address}
                  handleChange={(e) =>
                    setValues({ ...values, address: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInput
                  disabled
                  label={t("systemNumber")}
                  value={values.system_number}
                  handleChange={(e) =>
                    setValues({ ...values, system_number: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInput
                  label={t("systemSerialNumber")}
                  value={values.system_serial_number}
                  handleChange={(e) =>
                    setValues({
                      ...values,
                      system_serial_number: e.target.value,
                    })
                  }
                />
              </Grid>
              {role === roles.superadmin && (
                <Grid item xs={12} md={6}>
                  <CustomSelect
                    options={
                      (allCompanies || []).map((company) => ({
                        value: company.uid,
                        label: `${company.name} (${company.email})`,
                      })) || []
                    }
                    label={`${t("company")} (facoltativo)`}
                    value={values.azienda}
                    handleChange={(e) =>
                      setValues({ ...values, azienda: e.target.value })
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <CustomInput
                  disabled={role !== roles.respMaintainer}
                  label={t("idGestionale")}
                  value={values.id_az}
                  handleChange={(e) =>
                    setValues({ ...values, id_az: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          }
          actions={[
            <CustomButton
              fullWidth
              label={t("reset")}
              onClick={() =>
                setValues({
                  installation: "",
                  system_number: "",
                  system_serial_number: "",
                  address: "",
                  id_az: "",
                  model: "",
                  phone_operator: "",
                  azienda: "",
                })
              }
              confirm
              type="outlined"
            />,
            <CustomButton
              fullWidth
              label={t("ok")}
              onClick={handleSubmit}
              type="contained"
            />,
          ]}
        />
      </Grid>
    </Grid>
  );
};

export default UpdateElevator;
